import * as React from 'react';
import { format, parse } from 'date-fns';
import { vi } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import { vi as viLocale } from 'date-fns/locale';
import { Controller, useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';

interface DatePickerFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
  formatString?: string;
}

export const DatePickerField = React.forwardRef<HTMLDivElement, DatePickerFieldProps>(
  (
    {
      name,
      label,
      placeholder = 'Chọn ngày...',
      disabled = false,
      required = false,
      className,
      minDate,
      maxDate,
      formatString = 'dd/MM/yyyy',
    },
    ref
  ) => {
    const { control, formState: { errors } } = useFormContext();
    const error = errors[name];
    const [open, setOpen] = React.useState(false);

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const dateValue = field.value
              ? typeof field.value === 'string'
                ? parse(field.value, formatString, new Date())
                : field.value
              : undefined;

            return (
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !field.value && 'text-muted-foreground',
                      error && 'border-destructive'
                    )}
                    disabled={disabled}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {field.value
                      ? format(dateValue!, formatString, { locale: vi })
                      : placeholder}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={(date) => {
                      if (date) {
                        field.onChange(format(date, formatString));
                      } else {
                        field.onChange(null);
                      }
                      setOpen(false);
                    }}
                    disabled={(date) => {
                      if (minDate && date < minDate) return true;
                      if (maxDate && date > maxDate) return true;
                      return false;
                    }}
                    initialFocus
                    locale={vi as any}
                  />
                </PopoverContent>
              </Popover>
            );
          }}
        />
        {error && (
          <p className="text-sm text-destructive">{String(error.message)}</p>
        )}
      </div>
    );
  }
);

DatePickerField.displayName = 'DatePickerField';

export default DatePickerField;
